import type { GetAttachmentConversionResponse } from "@nlc/shared-types";

import { useQuery } from "@tanstack/react-query";
import * as gifuct from "gifuct-js";
import { useEffect, useRef, useState } from "react";

import { attachmentsKeys } from "@/hooks/queryKeys";
import apiClient from "@/lib/api/client";

import { useFeedbackTool } from "../../hooks/useFeedbackTool";
interface UseGifToMp4Result {
  error: null | string;
  isLoading: boolean;
  mp4Url: null | string;
}

export function useGif(url: string | undefined): UseGifToMp4Result {
  const [mp4Url, setMp4Url] = useState<null | string>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<null | string>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const { img } = useFeedbackTool();
  const attachmentId = img.id;

  const { data } = useQuery({
    queryFn: () =>
      apiClient
        .url(`/attachments/conversion/${attachmentId}`)
        .get()
        .json<GetAttachmentConversionResponse>(),
    queryKey: attachmentsKeys.getConversions(attachmentId),
  });

  useEffect(() => {
    if (!url?.endsWith(".gif")) return;

    const convertGifToMp4 = async () => {
      if (data?.length && data?.[0]?.url) {
        setMp4Url(data[0].url);
        setIsLoading(false);

        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(url);

        if (!response?.ok) throw new Error("Failed loading GIF");
        const arrayBuffer = await response?.arrayBuffer();

        const gif = await gifuct.parseGIF(arrayBuffer);
        const frames = await gifuct.decompressFrames(gif, true);

        if (frames.length === 0) {
          throw new Error("Frame not found GIF");
        }

        const canvas = document.createElement("canvas");

        canvasRef.current = canvas;
        const ctx = canvas.getContext("2d");

        if (!ctx) throw new Error("Context 2D not suported");

        const firstFrame = frames[0];
        const { height, width } = firstFrame!.dims;

        canvas.width = width;
        canvas.height = height;

        const stream = canvas.captureStream(30);

        mediaRecorderRef.current = new MediaRecorder(stream, {
          mimeType: "video/webm;codecs=vp8",
        });

        chunksRef.current = [];
        mediaRecorderRef.current.ondataavailable = (event) => {
          if (event.data.size > 0) {
            chunksRef.current.push(event.data);
          }
        };

        mediaRecorderRef.current.onstop = () => {
          const blob = new Blob(chunksRef.current, { type: "video/mp4" });

          setMp4Url(URL.createObjectURL(blob));
          setIsLoading(false);
        };

        mediaRecorderRef.current.start();

        for (const frame of frames) {
          const imageData = new ImageData(
            new Uint8ClampedArray(frame.patch),
            frame.dims.width,
            frame.dims.height,
          );

          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.putImageData(imageData, frame.dims.left, frame.dims.top);

          await new Promise((resolve) => setTimeout(resolve, frame.delay));
        }

        mediaRecorderRef.current.stop();
      } catch (error_) {
        setError(error_ instanceof Error ? error_.message : "Unknown error");
        setIsLoading(false);
      }
    };

    convertGifToMp4();

    return () => {
      if (mp4Url) {
        URL.revokeObjectURL(mp4Url);
      }
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== "inactive"
      ) {
        mediaRecorderRef.current.stop();
      }
    };
  }, [data, attachmentId]);

  return { error, isLoading, mp4Url };
}
